import { Dispatch, SetStateAction, ReactNode } from "react";

import {
    HeaderCard,
    MEDIA,
    Row,
    StatColumn,
    StatProps,
    TabInterface,
    TabsCard,
    Text,
    Icon,
    Button
} from "@bridgesplit/ui";
import { useMediaQuery } from "@mui/material";
import { formatPercent, Hidable } from "@bridgesplit/utils";
import { ArrowForwardOutlined } from "@mui/icons-material";
import { useHealthColor } from "app/components/common";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { COPY } from "app/constants";

import { CollateralLoanHealthChange } from "./types";

export function LoanActionHeader({ header, children }: { children: ReactNode; header: string }) {
    const isMobile = useMediaQuery(ACTION_BREAKPOINT.below);

    return (
        <HeaderCard
            spacing={2}
            wrapperSx={{ [ACTION_BREAKPOINT.above]: { py: 2, width: "auto" } }}
            childrenSx={{ [ACTION_BREAKPOINT.above]: { px: 2 } }}
            header={
                <Text sx={{ [ACTION_BREAKPOINT.above]: { px: 2 } }} variant={isMobile ? "h3" : "h4"}>
                    {header}
                </Text>
            }
        >
            {children}
        </HeaderCard>
    );
}

export function LoanActionTabs({ tabs }: { tabs: TabInterface[] }) {
    const isMobile = useMediaQuery(ACTION_BREAKPOINT.below);
    return (
        <TabsCard
            labelSx={{ [ACTION_BREAKPOINT.above]: { pt: 1 } }}
            bodySx={{ [ACTION_BREAKPOINT.above]: { p: 2, pt: 0 } }}
            childrenSpacing={2}
            variant={isMobile ? "standard" : "spaced-out"}
            textVariant={isMobile ? undefined : "h4"}
            tabs={tabs}
        />
    );
}

export const ACTION_BREAKPOINT = MEDIA.XL;

export function HealthStat({ healthChange }: { healthChange: CollateralLoanHealthChange | undefined }) {
    const originalColor = useHealthColor(healthChange?.originalHealthStatus);

    const newColor = useHealthColor(healthChange?.newHealthStatus);

    if (!healthChange) return null;

    return (
        <Row spacing={0.5}>
            <Text sx={{ color: originalColor }}>
                {formatPercent(healthChange?.originalHealth, { hideSymbol: true })}
            </Text>
            <Text color="disabled" variant="body2">
                <ArrowForwardOutlined />
            </Text>
            <Text sx={{ color: newColor }}>{formatPercent(healthChange?.newHealth)}</Text>
        </Row>
    );
}

export function CollateralLoanHealthChangeStats({
    healthChange,
    extraStats = [],
    isDefault = false
}: {
    healthChange: CollateralLoanHealthChange | undefined;
    extraStats?: Hidable<StatProps>[];
    isDefault?: boolean;
}) {
    const healthDelta: [number | undefined, number | undefined] = [
        healthChange?.originalCollateralValueUsd,
        healthChange?.newLoanCollateralValueUsd
    ];

    const stats: Hidable<StatProps>[] = [
        {
            value: <HealthStat healthChange={healthChange} />,
            caption: COPY.HEALTH_FACTOR_TERM,
            hide: isDefault
        },
        {
            value: healthDelta,
            caption: "Collateral value",
            isUsd: true
        },
        ...extraStats
    ].filter((stat) => !stat.hide);

    return <StatColumn loading={!healthChange} stats={stats} />;
}

export function ReceiveSwapButton<T extends { Collateral: 0; Principal: 1 }>({
    receiveType,
    setReceiveType,
    textVariant = "body2",
    principalToken,
    collateralToken,
    tokenPreText,
    disabled = false
}: {
    receiveType: T[keyof T];
    setReceiveType: Dispatch<SetStateAction<T[keyof T]>>;
    textVariant?: "body2" | "body1";
    principalToken?: TokenListMetadata;
    collateralToken?: TokenListMetadata;
    tokenPreText?: string;
    disabled?: boolean;
}) {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const swapToToken = receiveType === 1 ? collateralToken : principalToken;

    let receiveText = `${tokenPreText} ${swapToToken?.symbol}`;

    if (isMobile && receiveText.length > 18) {
        receiveText = `${receiveText.slice(0, 18)}...`;
    }

    return (
        <Button
            variant="text"
            color="secondary"
            noHover
            disabled={disabled}
            textProps={{ variant: textVariant }}
            onClick={() => setReceiveType((receiveType === 1 ? 0 : 1) as T[keyof T])}
        >
            <Icon type="swap" />
            {receiveText}
        </Button>
    );
}
